import {
  BlotterColumnKey,
  OrderColumnInfo,
  OrdersTableMetadata,
} from "@enfusion-ui/core";
import {
  ColumnTypeCategory,
  ComplianceCellValue,
  EnumCellValue,
  QuoteCellValue,
} from "@enfusion-ui/types";
import { formatCellValues, orderCellFormatter } from "@enfusion-ui/web-core";
import { ColDef, ValueGetterParams } from "ag-grid-enterprise";
import { parseISO } from "date-fns";
import { get } from "lodash";

import defaultColumnWidths from "./defaultColumnWidths";

function lookupType(columnTypeCategory: ColumnTypeCategory) {
  switch (columnTypeCategory) {
    case "AveragePrice":
    case "IntegerNumber":
    case "MarketPrice":
    case "Number":
    case "Percent":
    case "PercentFilled":
    case "Quotation":
    case "SignSensitiveNumber":
    case "SignSensitivePercent":
    case "SignedQuantity":
    case "SignedIntegerQuantity":
      return "numericColumn";

    case "Date":
    case "DateTime":
    case "Time":
      return "dateColumn";

    default:
      return "standardColumn";
  }
}

export const ALL_KEYS = [
  "Contract",
  "Txn",
  "Quantity",
  "Limit",
  "Stop",
  "Type",
  "TIF",
  "Bid",
  "Ask",
  "Last",
  "ChangePct",
  "Change",
  "Status",
  "Filled",
  "Open",
  "Broker",
  "Destination",
  "Strategy",
  "Compliance",
  "Position",
  "AvgPx",
  "FilledPct",
  "LastFillPx",
  "MarketLimitDelta",
  "VWAP",
  "Volume",
  "VWAPShares",
  "FIXOrderId",
  "OrderId",
  "Trader",
  "BBTicker",
  "Ticker",
  "Counterparty",
  "StatusText",
  "PBDExposure",
  "Exposure",
  "ExposureChange",
  "OrderDate",
  "Idle",
  "Method",
  "SwapOrder",
  "ExecVal_NAV",
  "OpenVal_NAV",
  "OrderVal_NAV",
  "ListId",
  "ListSeqNo",
  "ListTotalNo",
  "ListAlias",
  "BrokerAccount",
  "NotionalTradeAmount",
  "SubmissionStatus",
  "ReleaseDateTime",
  "PBDFilledQty",
  "TodayFilledQty",
  "NotionalFilledAmount",
  "IsStale",
  "Ccy",
  "BidAskDelta",
  "Exchange",
  "_30DAvgVol",
  "_3MAvgVol",
  "_5DAvgVol",
  "NotionalUnfilledAmount",
  "OrderPctOf30D_ADV",
  "FilledPctOf30D_ADV",
  "OpenPctOf30D_ADV",
  "OrderPctOf3M_ADV",
  "FilledPctOf3M_ADV",
  "UnfilledPctOf3M_ADV",
  "LastModifiedAt",
  "ImpactVWAP",
  "ImpactVWAPShares",
  "FilledPctOfVWAPShares",
  "FilledPctOfImpactVWAPShares",
  "AllocationInstructions",
  "CounterpartyMemo",
  "ExecutedExposureBPS",
  "NotionalTradeAmountUSD",
  "NotionalUnfilledAmountUSD",
  "NotionalFilledAmountUSD",
  "Underlier",
  "FillPriceDeviation",
  "SODOpenQuantity",
  "TodayFilledPct",
  "WorkflowState",
  "FundName",
  "ClearingAccount",
  "BookName",
  "PortfolioManager",
  "Instructions",
  "Resumed",
  "InstrumentSubtype",
  "FirstTransmittedDate",
  "CompletedDate",
  "FirstFilledDate",
  "LastFilledDate",
  "Undecided",
  "CompletionStatus",
  "isExecutionRoute",
  "ParentOrderId",
  "CommissionRate",
  "CommissionRateType",
  "CommissionTotal",
  "Committed",
  "IsCurrentlyScheduled",
  "TodayAvgPx",
  "TodayCommitted",
  "SettleToBookFXRate",
  "Proceeds",
  "OrderDays",
  "_20DAvgVol",
  "RemainingVolumeOf5DAvg",
  "ImpactVWAPAvgPxDelta",
  "ShortLocateAmountApproved",
  "ShortLocateAmountRequested",
  "ShortLocateAmountAssigned",
  "ArrivalPrice",
  "ArrivalPctChange",
  "MaturityDate",
  "RIC",
  "OrderCreationMethod",
  "CalculatorType",
  "IsForceCancel",
  "ISIN",
  "ShortInterest",
  "ShortInterestPctEquityFloat",
  "IsUnsolicitedCancel",
  "DarkPools",
  "ArrivalVWAP",
  "OpenPx",
  "PreviousClosePx",
  "ExchangeCountryCode",
  "ExchangeCountryName",
  "MarketLimitDeltaPct",
  "FXFixingDate",
  "FXFarForwardDate",
  "FXFarFixingDate",
  "ChangesToAcknowledge",
  "TradingDesk",
  "AllocationReason",
  "TradeReason",
  "IsActiveParent",
  "CreationPrice",
  "TodayFilledQtyPctNav",
  "StrategyBook",
  "UndisclosedShort",
  "Actions",
  "ForwardDate",
  "CCP",
  "FCM",
  "DealId",
] as BlotterColumnKey[];

const columnPreDefs = (
  type: string,
  columnTypeCategory: ColumnTypeCategory
): Partial<ColDef> => {
  if (type === "numericColumn") {
    return {
      filter: "agMultiColumnFilter",
      valueGetter: ({ data, colDef }) => {
        const value = colDef.field ? get(data, colDef.field) : null;
        if (value !== null && columnTypeCategory === "Quotation") {
          const val = value as QuoteCellValue;
          if (isNaN(val.value)) return null;
          return val.value;
        }

        return value;
      },
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    };
  } else if (type === "dateColumn") {
    return {
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterDate: Date, cellValue: string | null) => {
                if (cellValue === null) return -1;
                return parseISO(cellValue).getTime() - filterDate.getTime();
              },
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              comparator: (valueA: string, valueB: string) => {
                return parseISO(valueA).getTime() - parseISO(valueB).getTime();
              },
            },
          },
        ],
      },
      comparator: (valueA, valueB) =>
        parseISO(valueA).getTime() - parseISO(valueB).getTime(),
    };
  }
  return {
    enableRowGroup: true,
    filter: "agMultiColumnFilter",
    valueGetter: ({ data, colDef }: ValueGetterParams) => {
      const value = colDef.field ? get(data, colDef.field) : null;
      if (value === null || value === undefined) return null;
      if (columnTypeCategory === "Compliance")
        return (value as ComplianceCellValue).complianceState;
      if (columnTypeCategory === "Enum")
        return (value as EnumCellValue).description || "";
      return value;
    },
    filterParams: {
      filters: [
        {
          filter: "agTextColumnFilter",
          filterParams: {
            defaultOption: "startsWith",
          },
        },
        {
          filter: "agSetColumnFilter",
          filterParams: {
            caseSensitive: true,
          },
        },
      ],
    },
  };
};

function getColumnDef(
  key: keyof OrdersTableMetadata,
  metadata: OrdersTableMetadata,
  widthOverride?: number | null,
  sortOrder?: string | null,
  pinned?: string | null,
  group?: number | null
) {
  const { name, columnTypeCategory } = metadata[key];
  const type = lookupType(columnTypeCategory);
  const colDef: ColDef = {
    headerName: name,
    field: `${key}`,
    colId: key,
    type,
    width: widthOverride || defaultColumnWidths[key || "Idle"],
    sort:
      sortOrder === "Ascending"
        ? "asc"
        : sortOrder === "Decending"
        ? "desc"
        : null,
    pinned: pinned === "Left" ? "left" : pinned === "Right" ? "right" : null,
    rowGroup: group !== undefined ? true : false,
    rowGroupIndex: group !== undefined ? group : undefined,
    keyCreator: ({ value }) =>
      orderCellFormatter({
        value,
        context: { metadata },
        colDef: { colId: key },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any),
    ...columnPreDefs(type, columnTypeCategory),
  };

  if (columnTypeCategory === "Enum" || columnTypeCategory === "Compliance") {
    colDef.comparator = (valueA, valueB) => {
      const value1 = formatCellValues(columnTypeCategory, valueA);
      const value2 = formatCellValues(columnTypeCategory, valueB);
      if (!valueA) return -1;
      if (!valueB) return 1;
      if (value1 === value2) return 0;
      return value1 > value2 ? 1 : -1;
    };
  }

  if (key === "FilledPct") {
    return { cellRenderer: "percentageCellRenderer", ...colDef };
  }

  return colDef;
}

export function createOrderColumnDefs(
  columns: OrderColumnInfo[],
  metadata: OrdersTableMetadata
): ColDef[] {
  let addedControlColDef = false;
  const columnDefs: ColDef[] = columns.map((column: OrderColumnInfo) => {
    const key = column.key as BlotterColumnKey;
    if (metadata[key]) {
      return getColumnDef(
        key,
        metadata,
        column.width,
        column.sortOrder,
        column.pinned,
        column.group
      );
    }

    /*
     * If metadata is not found for the key, then is most likely added from the UI -- like Actions
     * Just provide basic column defs unless key specific logic is known
     */

    const nonMetaColDef: ColDef = {
      headerName: key,
      colId: key,
      type: lookupType("Standard"),
      width: defaultColumnWidths[key],
    };

    if (column.key === "_Control_") {
      addedControlColDef = true;
      return {
        ...nonMetaColDef,
        headerName: "Actions",
        colId: "_Control_",
        cellRenderer: "transmitCancelCellRenderer",
        suppressMenu: true,
        sortable: false,
        pinned: "right",
      };
    }

    return nonMetaColDef;
  });

  if (!addedControlColDef) {
    columnDefs.push({
      hide: true,
      width: 100,
      headerName: "Actions",
      colId: "_Control_",
      cellRenderer: "transmitCancelCellRenderer",
      suppressMenu: true,
      sortable: false,
      pinned: "right",
    });
  }

  const currentColumns = columnDefs.reduce(
    (res, column) => res.add(column.colId || ""),
    new Set<string>()
  );
  const data = Object.keys(metadata).reduce((res, key) => {
    if (currentColumns.has(key)) return res;
    return [
      ...res,
      {
        ...getColumnDef(key as keyof OrdersTableMetadata, metadata),
        hide: true,
      },
    ];
  }, [] as ColDef[]);

  return [...columnDefs, ...data];
}
